import apiClient from './axios';

export type KnowledgeGapStatus = 'pending' | 'in_review' | 'approved' | 'rejected';

export type KnowledgeCategory =
  | 'cbn_regulation'
  | 'nigerian_tax'
  | 'investment_products'
  | 'personal_finance'
  | 'payiq_product'
  | 'banking_basics'
  | 'fraud_prevention'
  | 'business_tax'
  | 'business_accounting'
  | 'payiq_business';

/**
 * Which assistant flagged the gap, and therefore who the answer is written
 * for. The two share one knowledge base; the backend ingests an approved gap
 * at its own audience, so this is not something to pick at approval time —
 * it is something to write for.
 */
export type KnowledgeAudience = 'consumer' | 'business' | 'both';

/**
 * The categories worth offering per audience.
 *
 * Filing a business question under `personal_finance` mislabels it for
 * everyone who reads the queue afterwards, and the tax rules genuinely
 * differ — company income tax is not personal income tax with a bigger
 * number. The last two in each list are the genuinely shared ones.
 */
export const CATEGORIES_BY_AUDIENCE: Record<
  KnowledgeAudience,
  KnowledgeCategory[]
> = {
  consumer: [
    'personal_finance',
    'investment_products',
    'banking_basics',
    'fraud_prevention',
    'payiq_product',
    'nigerian_tax',
    'cbn_regulation',
  ],
  business: [
    'business_tax',
    'business_accounting',
    'payiq_business',
    'nigerian_tax',
    'cbn_regulation',
  ],
  both: ['nigerian_tax', 'cbn_regulation', 'banking_basics'],
};

export interface KnowledgeGap {
  id: string;
  userId: string;
  sessionId: string | null;
  userQuestion: string;
  reason: string;
  categoryGuess: KnowledgeCategory | null;
  audience: KnowledgeAudience;
  /** Set only on business gaps. */
  businessId: string | null;
  status: KnowledgeGapStatus;
  adminResponse: string | null;
  knowledgeDocumentId: string | null;
  occurrenceCount: number;
  createdAt: string;
  updatedAt: string;
}

export const knowledgeGapsApi = {
  async list(params: {
    page?: number;
    limit?: number;
    status?: KnowledgeGapStatus;
    audience?: KnowledgeAudience;
  }): Promise<{
    items: KnowledgeGap[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const res = await apiClient.get('/admin/knowledge-gaps', { params });
    return res.data.data;
  },

  /** Reject — dismisses the gap, no ingestion. */
  async reject(id: string, adminNote?: string): Promise<KnowledgeGap> {
    const res = await apiClient.patch(`/admin/knowledge-gaps/${id}`, {
      status: 'rejected',
      adminNote,
    });
    return res.data.data;
  },

  /** Approve — ingests title/category/content as a new knowledge document,
   *  closing the loop so future similar questions retrieve it via RAG. */
  async approve(
    id: string,
    payload: { title: string; category: KnowledgeCategory; content: string; adminNote?: string },
  ): Promise<KnowledgeGap> {
    const res = await apiClient.patch(`/admin/knowledge-gaps/${id}`, {
      status: 'approved',
      ...payload,
    });
    return res.data.data;
  },
};
